<template>
    <v-modal
        title="Edit Grid"
        scroll
        @close="$emit('close')"
        @submit="save">
        <template slot="contents">
            <r-tabs :divided="true" content-class="no-padding" menu-class="no-margin-bottom">
                <r-tab
                    name="Select Columns"
                    :active="true">
                    <div class="modal-content">
                        <p class="stack-m">Click the checkboxes to add or remove columns from the grid.</p>
                        <r-input
                            label="Search Columns"
                            v-model="query"
                            :auto-focus="true"
                        />
                    </div>
                    <div class="modal-content" :class="{'is-scrollable' : searchResults.length > 6}">
                        <ul class="list list-group list-relaxed">
                            <li class="list-item" v-for="column in searchResults">
                                <v-checkbox
                                    :key="column.id"
                                    :label="column.label"
                                    :value="column.id"
                                    v-model="selectedColumnIds"
                                    :disabled="column.sticky || isLastChecked(column.checked)"
                                />
                            </li>
                        </ul>
                    </div>
                </r-tab>
                <r-tab
                    name="Arrange Columns">
                    <div class="modal-content">
                        Drag the columns to rearrange their order in the grid.
                    </div>
                    <div class="modal-content" :class="{'is-scrollable' : selectedColumnList.length > 6}">
                        <draggable
                            v-bind="draggableOptions"
                            v-model="selectedColumnIds"
                            tag="ol"
                            @end="setFocus"
                            tabindex="0"
                            class="list list-group list-divided list-draggable">
                            <li class="list-item align-items-center"
                                v-for="(column, index) in selectedColumnList"
                                :key="column.id"
                                :class="{'is-draggable': !column.sticky}">
                                <span class="no-flex inline-s">
                                    <v-icon icon="drag"/>
                                </span>
                                <span class="list-item-content inline-l">
                                    <span class="display-flex align-items-center">
                                        <span>{{(index + 1)}}.&nbsp;</span>
                                        {{column.label}}
                                    </span>
                                </span>
                            </li>
                        </draggable>
                    </div>
                </r-tab>
            </r-tabs>
        </template>

        <template slot="left-actions">
            <v-popper2 position="topStart">
                <template #trigger="{popper}">
                    <button class="btn has-icon no-flex" @click="popper.toggle">
                        Use defaults...
                        <v-icon icon="caret-down"/>
                    </button>
                </template>
                <template #content="{popper}">
                    <div class="dropdown">
                        <div class="dropdown-content">
                            <a class="dropdown-item"
                               @click="resetColumns(popper)">
                                Reset to Default Columns
                            </a>
                        </div>
                        <div class="dropdown-content">
                            <a class="dropdown-item"
                               @click="resetOrder(popper)">
                                Reset to Default Order
                            </a>
                        </div>
                    </div>
                </template>
            </v-popper2>
        </template>
        <template slot="actions">
            <r-button type="primary" @click="save">Apply</r-button>
        </template>
    </v-modal>
</template>

<script>
    import vModal from '@/components/modal/v-modal';
    import {
        vCheckbox, vIcon, vIconButton,
    } from '@/components/form';
    import filterArrayByQuery from '@/common/helpers/filter-array-by-query';
    import draggable from 'vuedraggable';
    import draggableDefaults from '@/vendor/draggable/defaults';
    import vPopper2 from '@/components/popper/v-popper2';

    export default {
        name: 'column-selection-modal',
        components: {
            vModal,
            vCheckbox,
            vIcon,
            vIconButton,
            draggable,
            vPopper2,
        },
        props: {
            columnSet: {
                type: Object,
            },
        },
        computed: {
            selectableColumnList() {
                return Object.values(this.selectableColumns)
                    .map(({header: label, id, sticky}) => ({
                        id,
                        label,
                        checked: this.selectedColumnIds.includes(id),
                        sticky,
                    }));
            },
            selectedColumnList() {
                return this.selectedColumnIds
                    .map((id) => {
                        const {header: label, sticky} = this.selectableColumns[id];
                        return {
                            id,
                            label,
                            sticky,
                        };
                    });
            },
            // populates the select list when search is made
            searchResults() {
                if (this.results && this.results.length) {
                    return this.results;
                }
                return this.selectableColumnList;
            },
        },
        data() {
            return {
                query: '',
                selectedColumnIds: this.columnSet.getSelectedColumnIds(),
                selectableColumns: this.columnSet.getSelectableColumns(),
                draggableOptions: {
                    ...draggableDefaults,
                    draggable: '.is-draggable',
                    handle: null,
                },
            };
        },
        subscriptions() {
            const results = this.$watchAsObservable('query')
                .pluck('newValue')
                .debounceTime(300)
                .distinctUntilChanged()
                .map(this.findColumnByName)
                .defaultIfEmpty(this.selectableColumnList);
            return {
                results,
            };
        },
        methods: {
            // shared
            save() {
                this.columnSet.setSelectedColumnIds(this.selectedColumnIds);
                this.$emit('close');
            },
            resetColumns(popper = null) {
                this.selectedColumnIds = this.columnSet.getDefaultColumnIds();
                this.mode = 'select';
                if (popper) {
                    popper.close();
                }
            },
            resetOrder(popper = null) {
                this.selectedColumnIds = Object.keys(this.selectableColumns)
                    .filter(id => this.selectedColumnIds.includes(id));
                this.mode = 'arrange';
                if (popper) {
                    popper.close();
                }
            },
            // draggable clears keyboard focus
            // we need keyboard focus in order to save on enter
            setFocus(event) {
                event.to.focus();
            },
            findColumnByName(term) {
                return filterArrayByQuery({
                    term,
                    array: this.selectableColumnList,
                    value: column => column.label,
                });
            },
            isLastChecked(checked) {
                return this.selectedColumnList.length === 1 && checked;
            },
        },
    };
</script>
